import React from 'react';
import './buttons.scss';

/**
 * FancyButton — the canonical button for the app.
 *
 * Props:
 *   variant: 'primary' | 'secondary' | 'danger' | 'ghost' | 'text'  (default 'primary')
 *   size:    'sm' | 'md' | 'lg'                                     (default 'md')
 *   startIcon, endIcon: ReactNode
 *   loading: boolean — shows a spinner and disables click
 *   disabled: boolean
 *   fullWidth: boolean
 *   pill: boolean — fully-rounded shape
 *   type: 'button' | 'submit' | 'reset'  (default 'button')
 *   onClick, style, className, children
 */
export const FancyButton = React.forwardRef(function FancyButton(
  {
    variant = 'primary',
    size = 'md',
    startIcon,
    endIcon,
    loading = false,
    disabled = false,
    fullWidth = false,
    pill = false,
    type = 'button',
    onClick,
    style,
    className = '',
    children,
    ...rest
  },
  ref
) {
  const isDisabled = disabled || loading;

  const classes = [
    'fancy-btn',
    `fancy-btn--${variant}`,
    `fancy-btn--${size}`,
    pill ? 'fancy-btn--pill' : '',
    fullWidth ? 'fancy-btn--full' : '',
    loading ? 'is-loading' : '',
    isDisabled ? 'is-disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      style={style}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...rest}
    >
      <span className="fancy-btn__shine" aria-hidden="true" />
      {loading ? (
        <span className="fancy-btn__spinner" aria-hidden="true" />
      ) : startIcon ? (
        <span className="fancy-btn__icon fancy-btn__icon--start">{startIcon}</span>
      ) : null}
      {children != null && children !== false && (
        <span className="fancy-btn__label">{children}</span>
      )}
      {!loading && endIcon && (
        <span className="fancy-btn__icon fancy-btn__icon--end">{endIcon}</span>
      )}
    </button>
  );
});
